import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFplId } from "../context/FplIdContext";

/**
 * First-run full-screen landing.
 * Primary path: enter FPL ID -> validate -> confirm team -> go to /my-team
 * Secondary path: "Browse stats instead" -> skip() -> go to /
 * Shown only when the user hasn't decided yet (see guard in App.js).
 */
export default function Onboarding() {
  const { validateId, setFplId, skip } = useFplId();
  const navigate = useNavigate();

  const [id, setId] = useState("");
  const [status, setStatus] = useState("idle"); // idle | checking | confirm | error
  const [error, setError] = useState("");
  const [found, setFound] = useState(null); // { id, info }

  const check = async () => {
    setError("");
    setStatus("checking");
    try {
      const result = await validateId(id);
      setFound(result);
      setStatus("confirm");
    } catch (e) {
      setError(e.message || "Something went wrong. Try again.");
      setStatus("error");
    }
  };

  const confirm = () => {
    setFplId(found.id, found.info);
    navigate("/my-team");
  };

  const browse = () => {
    skip();
    navigate("/");
  };

  const onKey = (e) => {
    if (e.key === "Enter" && id.trim() && status !== "checking") check();
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 bg-gradient-to-br from-[#0d0620] via-[#37003c] to-[#0a0416] text-white">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl grid place-items-center text-3xl bg-gradient-to-br from-[#dc143c] to-[#8b0f26] shadow-lg shadow-[#dc143c]/30">
            🏔️
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            FPL <span className="text-[#ff2b52]">Nepal</span>
          </h1>
          <p className="text-white/60 text-sm mt-1">Season 26/27</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-6">
          {status !== "confirm" ? (
            <>
              <h2 className="text-lg font-semibold mb-1">See your live gameweek</h2>
              <p className="text-white/60 text-sm mb-5">
                Enter your FPL ID to track your points, rank and players in real time.
              </p>

              <label className="block text-xs font-medium text-white/50 mb-2 uppercase tracking-wider">
                Your FPL ID
              </label>
              <input
                inputMode="numeric"
                value={id}
                onChange={(e) => {
                  setId(e.target.value.replace(/[^\d]/g, ""));
                  if (status === "error") setStatus("idle");
                }}
                onKeyDown={onKey}
                placeholder="e.g. 1234567"
                className="w-full rounded-xl bg-black/30 border border-white/15 px-4 py-3 text-lg outline-none focus:border-[#ff2b52] transition"
              />

              {status === "error" && (
                <p className="text-[#ff8a9b] text-sm mt-2">{error}</p>
              )}

              <button
                onClick={check}
                disabled={!id.trim() || status === "checking"}
                className="mt-4 w-full rounded-xl bg-[#dc143c] hover:bg-[#ff2b52] disabled:opacity-40 disabled:cursor-not-allowed py-3 font-semibold transition shadow-lg shadow-[#dc143c]/25"
              >
                {status === "checking" ? "Checking…" : "See my team"}
              </button>

              {/* Quiet secondary path */}
              <button
                onClick={browse}
                className="mt-3 w-full text-center text-sm text-white/50 hover:text-white/80 transition"
              >
                Browse stats instead →
              </button>

              {/* Help */}
              <details className="mt-5 text-sm text-white/50">
                <summary className="cursor-pointer hover:text-white/70">
                  Where do I find my FPL ID?
                </summary>
                <p className="mt-2 leading-relaxed">
                  Log in at fantasy.premierleague.com, open the <b>Points</b> tab, and
                  copy the number in the address bar:
                  <br />
                  <span className="text-white/70">
                    …/entry/<b className="text-[#ff2b52]">1234567</b>/event/1
                  </span>
                </p>
              </details>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold mb-1">Is this you?</h2>
              <div className="rounded-xl bg-black/30 border border-white/10 p-4 my-4">
                <div className="text-xl font-bold">{found.info.team_name || "Your team"}</div>
                <div className="text-white/60 text-sm">{found.info.manager_name}</div>
                <div className="text-white/40 text-xs mt-1">ID {found.id}</div>
              </div>
              <button
                onClick={confirm}
                className="w-full rounded-xl bg-[#dc143c] hover:bg-[#ff2b52] py-3 font-semibold transition shadow-lg shadow-[#dc143c]/25"
              >
                Yes, continue →
              </button>
              <button
                onClick={() => {
                  setStatus("idle");
                  setFound(null);
                }}
                className="mt-3 w-full text-center text-sm text-white/50 hover:text-white/80 transition"
              >
                No, try a different ID
              </button>
            </>
          )}
        </div>

        <p className="text-center text-white/30 text-xs mt-6">
          Your ID is saved on this device only. You can add or change it anytime.
        </p>
      </div>
    </div>
  );
}
